<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  quote: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

const { t } = useI18n();
const dialogRef = ref(null);
const acceptanceReason = ref('');

const canConfirm = computed(
  () => !!acceptanceReason.value.trim() && !props.isLoading
);

const quoteLabel = computed(() => {
  if (!props.quote) return '';
  return (
    props.quote.name ||
    t('PLANNING.SHOW.QUOTE_VERSION_SHORT', {
      version: props.quote.version,
    })
  );
});

watch(
  () => props.modelValue,
  async open => {
    if (open) {
      acceptanceReason.value = '';
      await nextTick();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  },
  { immediate: true }
);

const handleConfirm = () => {
  const reason = acceptanceReason.value.trim();
  if (!reason || props.isLoading) return;
  emit('confirm', reason);
};

const handleClose = reason => {
  emit('update:modelValue', false);
  if (reason === 'cancel' || reason === 'dismiss' || reason == null) {
    emit('cancel');
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    width="md"
    :title="t('PLANNING.SHOW.MARK_ACCEPTED_MODAL.TITLE')"
    :description="
      t('PLANNING.SHOW.MARK_ACCEPTED_MODAL.DESCRIPTION', { quote: quoteLabel })
    "
    :confirm-button-label="t('PLANNING.SHOW.CONFIRM_MARK_ACCEPTED')"
    :cancel-button-label="t('PLANNING.EVENTS.CANCEL')"
    :disable-confirm-button="!canConfirm"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="handleClose"
    @cancel="handleClose"
  >
    <div class="flex flex-col gap-2">
      <label
        class="block text-sm font-medium text-n-slate-11"
        for="quote-acceptance-reason"
      >
        {{ t('PLANNING.SHOW.ACCEPTANCE_REASON') }}
        <span class="text-r-500">*</span>
      </label>
      <TextArea
        id="quote-acceptance-reason"
        v-model="acceptanceReason"
        :placeholder="t('PLANNING.SHOW.MARK_ACCEPTED_MODAL.REASON_PLACEHOLDER')"
        :disabled="isLoading"
        :max-length="500"
        auto-height
        min-height="5rem"
      />
    </div>
  </Dialog>
</template>
